import { Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { dm, dmSv, when } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import { Avatar, ConfirmDialog, ErrorBox, Loading, PageHead, toast } from '../ui.js';
import { monthLabel } from './Projects.js';
import { ClientStartCell } from './clientStart.js';
import { AccessRequests } from './accessRequests.js';

/**
 * Clients overview (owner 2026-10-16, "Lista och faktaband"): the list shows each client's agreement, project status,
 * next deadline, what waits on the client and who has gone quiet; the client card opens with the same facts.
 * Agreements show only to those who see prices.
 */

type ProjectStatus = 'planned' | 'pre_production' | 'in_production' | 'in_review' | 'delivering' | 'delivered' | 'closed';
interface Named {
  en: string;
  sv: string;
}

export interface AgreementFact {
  id: string;
  ref: string;
  title: string;
  state: 'active' | 'quote' | 'ended';
  kind: 'subscription' | 'project';
  packageKey: string;
  /** The package's level, e.g. Medium */
  packageLevel: Named | null;
  callOffKeys: string[];
  minProductions: number | null;
  monthly: number;
  options: Named[];
  signedOn: string | null;
  sentAt: string | null;
  validUntil: string | null;
  endedOn: string | null;
  period: {
    start: string;
    end: string | null;
    noticeBy: string | null;
    phase: 'upcoming' | 'binding' | 'rolling' | 'period' | 'period_over';
    progress: number | null;
    actBy: string | null;
    renewSoon: boolean;
  } | null;
}

export interface ClientFacts {
  producer: { id: string; name: string } | null;
  projects: { total: number; planned: number; active: number; delivered: number };
  byStatus: Partial<Record<ProjectStatus, number>>;
  waitingOnClient: number;
  nextDeadline: string | null;
  lastDelivery: string | null;
  firstMonth: string | null;
  quiet: boolean;
  /** Absent when this person does not see agreements */
  agreement?: AgreementFact | null;
}

type Lifecycle = 'prospect' | 'active' | 'project_based' | 'paused' | 'inactive' | 'out';
interface ClientRow extends ClientFacts {
  id: string;
  name: string;
  code: string;
  lifecycle?: Lifecycle;
  notes: number;
  lastNoteAt: string | null;
  /** a new client still starting: the bar's steps done and the next step (0–4); absent once running (2026-10-28) */
  start?: { kind?: 'new' | 'hub'; done: number; of?: number; next: number; producer: string | null } | null;
}
/** What the Monday import would bring in (dryRun) or brought in (owner 2026-10-28) */
interface MondayPlan {
  clients: { read: number; created: number; updated: number; linked: string[]; unchanged: number; duplicates: string[]; byLifecycle: Partial<Record<Lifecycle, number>>; newNames: string[] };
  contacts: { created: number; former: number; known: number; noEmail: number; noClient: string[] };
  notes?: { updates: number; created: number; known: number; noClient: string[] };
  agreements?: { contracts: number; files: number };
}
interface NotesStep {
  created: number;
  cursor: string | null;
}
/** Prospects and former clients (most of what came from Monday) stay out of the list until asked for, unless something runs */
const current = (c: ClientRow) => !['prospect', 'inactive', 'out'].includes(c.lifecycle ?? 'active') || c.projects.planned + c.projects.active > 0;

const pl = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const TX = texts(
  {
    clients: 'Clients',
    sub: (n: number, active: number | null) => `${pl(n, 'client', 'clients')}${active === null ? '' : ` · ${active} with an active agreement`} · open one to see its projects and every note`,
    attention: 'Show clients that need attention',
    statActive: ['Active agreements', 'signed and in force'] as [string, string],
    statRenew: ['Renew soon', 'notice day or period end within 90 days'] as [string, string],
    statWaiting: 'Waiting on the client',
    waitingD: (p: number, c: number) => (c ? `${pl(p, 'project', 'projects')} at ${pl(c, 'client', 'clients')}` : 'no project waits on a client'),
    statQuiet: ['Quiet', 'nothing ongoing for 2+ months'] as [string, string],
    search: 'Search',
    nameOrCode: 'Client name',
    show: 'Show',
    filters: { all: 'All', starting: 'Not running yet', active: 'Active agreement', renew: 'Renew soon', none: 'No agreement', waiting: 'Waiting on client', quiet: 'Quiet' },
    sort: 'Sort',
    sorts: { name: 'Name', next: 'Next deadline', ongoing: 'Most ongoing projects', agreement: 'Agreement date' },
    noMatch: (q: string) => `No client matches “${q}”.`,
    noneInView: 'No client in this view.',
    cols: { client: 'Client', agreement: 'Agreement', projects: 'Projects', next: 'Next deadline', waiting: 'Waiting on client', last: 'Last delivery' },
    notes: (n: number, at: string) => `${pl(n, 'note', 'notes')}, latest ${at}`,
    noNotes: 'no notes',
    projectsLine: (ongoing: number, delivered: number) => `${ongoing} ongoing · ${delivered} delivered`,
    noProjects: 'No projects',
    today: 'Today',
    nProjects: (n: number) => pl(n, 'project', 'projects'),
    quiet: 'Quiet',
    former: 'Show prospects and former clients',
    tag: { prospect: 'Prospect', inactive: 'Former', out: 'Former', paused: 'Paused' } as Partial<Record<Lifecycle, string>>,
    fromMonday: 'Get from Monday',
    importTitle: 'Get clients, contacts and notes from Monday',
    importClients: (r: MondayPlan['clients']) =>
      `${pl(r.created, 'new client', 'new clients')}${r.created ? ` (${r.byLifecycle.prospect ?? 0} prospects, ${(r.byLifecycle.out ?? 0) + (r.byLifecycle.inactive ?? 0)} former)` : ''}, ${r.updated} known clients completed with org. no, address, invoice details or producer${r.linked.length ? `, ${r.linked.length} linked by name` : ''}.`,
    importContacts: (r: MondayPlan['contacts']) => `${pl(r.created, 'contact', 'contacts')}${r.former ? ` (${r.former} former)` : ''}; ${r.known} already here, ${r.noEmail} without an email address.`,
    importAgreements: (r: NonNullable<MondayPlan['agreements']>) =>
      `Agreement details on ${pl(r.contracts, 'client', 'clients')} and ${pl(r.files, 'agreement file', 'agreement files')}, copied to Drive (Avtal/{client}/Från Monday) a few at a time while the agreements folder is on in Settings.`,
    importNotes: (r: NonNullable<MondayPlan['notes']>) => `${pl(r.created, 'note', 'notes')} from Client Notes${r.known ? ` (${r.known} already here)` : ''}${r.noClient.length ? `; ${r.noClient.length} rows match no client: ${r.noClient.slice(0, 8).join(', ')}${r.noClient.length > 8 ? ' …' : ''}` : ''}.`,
    importDup: (names: string[]) => `Twice in Monday (both come in): ${names.join(', ')}.`,
    importSafe: 'Prospects and former clients are kept out of the list until you tick “Show prospects and former clients”. Nothing is changed in Monday, and running it again only brings what is new.',
    importGo: 'Import',
    importNotesNow: (n: number) => `Clients and contacts are in. Notes: ${n} so far …`,
    imported: (c: number, k: number, n: number, h: number) => `From Monday: ${pl(c, 'client', 'clients')}, ${pl(k, 'contact', 'contacts')}, ${pl(n, 'note', 'notes')} and ${pl(h, 'earlier project', 'earlier projects')}`,
    importProjectsNow: (n: number) => `Notes are in. Earlier projects: ${n} so far …`,
    // agreement line
    noAgreement: 'No agreement',
    quoteSent: (d: string) => `Quote sent${d ? ` ${d}` : ''}`,
    validTo: (d: string) => `Valid to ${d}`,
    ended: (d: string) => `Ended${d ? ` ${d}` : ''}`,
    subscription: 'Subscription',
    perProduction: 'Per production',
    starts: (d: string) => `Starts ${d}`,
    noticeBy: (d: string) => `Notice by ${d}`,
    bindsTo: (d: string) => `binds to ${d}`,
    BindsTo: (d: string) => `Binds to ${d}`,
    untilNotice: 'Runs until notice',
    periodEnded: (d: string) => `Period ended ${d}`,
    periodEnds: (d: string) => `Period ends ${d}`,
    minBy: (n: number, d: string) => `Min ${n} by ${d}`,
    runsTo: (d: string) => `Runs to ${d}`,
    // client card
    stages: ['Planned', 'Pre-prod', 'Production', 'Review', 'Delivered'],
    agreement: 'Agreement',
    agreementRef: (r: string) => `Agreement ${r}`,
    noAgreementYet: 'No agreement or quote yet.',
    quotes: 'Quotes',
    createQuote: 'Create quote',
    projectStatus: 'Project status',
    projects: 'Projects',
    lastDelivery: (m: string) => `Last delivery ${m}`,
    nothingDelivered: 'Nothing delivered yet',
    nextDeadline: (d: string) => `next deadline ${d}`,
    todayLower: 'today',
    clientSince: (m: string) => `client since ${m}`,
    needsDoing: 'Needs doing',
    nothingToDo: 'Nothing needs doing right now.',
    todo: {
      periodOver: (d: string) => `The call-off period ended ${d}: settle the minimum and agree what comes next`,
      renew: (d: string) => `Decide on renewal: notice must be given by ${d}`,
      periodEnds: (d: string) => `The call-off period ends ${d}: offer a new agreement`,
      quote: (ref: string, d: string | null) => `Quote ${ref} waits for an answer${d ? `, valid to ${d}` : ''}`,
      ended: (d: string | null) => `The agreement ended${d ? ` ${d}` : ''}: offer a new one?`,
      deadlineToday: 'A project has a deadline today',
      waiting: (n: number) => `${pl(n, 'project is', 'projects are')} waiting on the client`,
      noContact: 'No contact person, so briefs and the client portal cannot be sent',
      quietSince: (m: string) => `Nothing ongoing since ${m}: time to check in?`,
      noProjects: 'No projects yet',
      noBrief: 'The internal client brief is not written'
    },
    states: { active: 'Signed', quote: 'Quote sent', ended: 'Ended' },
    rows: { type: 'Type', price: 'Price', minimum: 'Minimum', term: 'Minimum term', period: 'Period', notice: 'Notice', validTo: 'Valid to', options: 'Options' },
    perMonth: (kr: string) => `${kr} / month`,
    minimum: (n: number, pkg: string) => `${pl(n, 'production', 'productions')} of ${pkg}`,
    fromUntil: (d: string) => `From ${d}, until further notice`,
    notice: (d: string) => `3 months · by ${d} to end with the term`,
    passed: (pct: number, sub: boolean) => `${pct} % of the ${sub ? 'minimum term' : 'period'} has passed`
  },
  {
    clients: 'Kunder',
    sub: (n: number, active: number | null) => `${n} kunder${active === null ? '' : ` · ${active} med aktivt avtal`} · öppna en för att se dess projekt och alla anteckningar`,
    attention: 'Visa kunder som behöver något',
    statActive: ['Aktiva avtal', 'signerade och gällande'],
    statRenew: ['Förnyas snart', 'uppsägningsdag eller periodslut inom 90 dagar'],
    statWaiting: 'Väntar på kunden',
    waitingD: (p: number, c: number) => (c ? `${p} projekt hos ${pl(c, 'kund', 'kunder')}` : 'inget projekt väntar på en kund'),
    statQuiet: ['Tysta', 'inget pågående på 2+ månader'],
    search: 'Sök',
    nameOrCode: 'Kundnamn',
    show: 'Visa',
    filters: { all: 'Alla', starting: 'Inte igång än', active: 'Aktivt avtal', renew: 'Förnyas snart', none: 'Inget avtal', waiting: 'Väntar på kund', quiet: 'Tysta' },
    sort: 'Sortera',
    sorts: { name: 'Namn', next: 'Nästa deadline', ongoing: 'Flest pågående projekt', agreement: 'Avtalsdatum' },
    noMatch: (q: string) => `Ingen kund matchar ”${q}”.`,
    noneInView: 'Ingen kund i den här vyn.',
    cols: { client: 'Kund', agreement: 'Avtal', projects: 'Projekt', next: 'Nästa deadline', waiting: 'Väntar på kund', last: 'Senaste leverans' },
    notes: (n: number, at: string) => `${pl(n, 'anteckning', 'anteckningar')}, senast ${at}`,
    noNotes: 'inga anteckningar',
    projectsLine: (ongoing: number, delivered: number) => `${ongoing} pågående · ${delivered} levererade`,
    noProjects: 'Inga projekt',
    today: 'I dag',
    nProjects: (n: number) => `${n} projekt`,
    quiet: 'Tyst',
    former: 'Visa potentiella och tidigare kunder',
    tag: { prospect: 'Potentiell kund', inactive: 'Tidigare', out: 'Tidigare', paused: 'Pausad' } as Partial<Record<Lifecycle, string>>,
    fromMonday: 'Hämta från Monday',
    importTitle: 'Hämta kunder, kontakter och anteckningar från Monday',
    importClients: (r: MondayPlan['clients']) =>
      `${r.created} nya kunder${r.created ? ` (varav ${r.byLifecycle.prospect ?? 0} potentiella och ${(r.byLifecycle.out ?? 0) + (r.byLifecycle.inactive ?? 0)} tidigare)` : ''}, ${r.updated} befintliga kompletteras med org.nr, adress, fakturauppgifter eller producent${r.linked.length ? `, ${r.linked.length} kopplas på namn` : ''}.`,
    importContacts: (r: MondayPlan['contacts']) => `${pl(r.created, 'kontakt', 'kontakter')}${r.former ? ` (varav ${r.former} tidigare)` : ''}; ${r.known} finns redan, ${r.noEmail} saknar e-post.`,
    importAgreements: (r: NonNullable<MondayPlan['agreements']>) =>
      `Avtalsuppgifter på ${pl(r.contracts, 'kund', 'kunder')} och ${pl(r.files, 'avtalsfil', 'avtalsfiler')}, som kopieras till Drive (Avtal/{kund}/Från Monday) några i taget när avtalsmappen är på i Inställningar.`,
    importNotes: (r: NonNullable<MondayPlan['notes']>) => `${pl(r.created, 'anteckning', 'anteckningar')} från Client Notes${r.known ? ` (${r.known} finns redan)` : ''}${r.noClient.length ? `; ${r.noClient.length} rader matchar ingen kund: ${r.noClient.slice(0, 8).join(', ')}${r.noClient.length > 8 ? ' …' : ''}` : ''}.`,
    importDup: (names: string[]) => `Finns två gånger i Monday (båda kommer in): ${names.join(', ')}.`,
    importSafe: 'Potentiella och tidigare kunder syns inte i listan förrän du kryssar i ”Visa potentiella och tidigare kunder”. Inget ändras i Monday, och en ny körning hämtar bara det som är nytt.',
    importGo: 'Importera',
    importNotesNow: (n: number) => `Kunder och kontakter är inne. Anteckningar: ${n} hittills …`,
    imported: (c: number, k: number, n: number, h: number) => `Från Monday: ${pl(c, 'kund', 'kunder')}, ${pl(k, 'kontakt', 'kontakter')}, ${pl(n, 'anteckning', 'anteckningar')} och ${pl(h, 'tidigare projekt', 'tidigare projekt')}`,
    importProjectsNow: (n: number) => `Anteckningarna är inne. Tidigare projekt: ${n} hittills …`,
    noAgreement: 'Inget avtal',
    quoteSent: (d: string) => `Offert skickad${d ? ` ${d}` : ''}`,
    validTo: (d: string) => `Giltig till ${d}`,
    ended: (d: string) => `Avslutat${d ? ` ${d}` : ''}`,
    subscription: 'Abonnemang',
    perProduction: 'Per produktion',
    starts: (d: string) => `Startar ${d}`,
    noticeBy: (d: string) => `Säg upp senast ${d}`,
    bindsTo: (d: string) => `binder till ${d}`,
    BindsTo: (d: string) => `Binder till ${d}`,
    untilNotice: 'Löper tills vidare',
    periodEnded: (d: string) => `Perioden slutade ${d}`,
    periodEnds: (d: string) => `Perioden slutar ${d}`,
    minBy: (n: number, d: string) => `Minst ${n} till ${d}`,
    runsTo: (d: string) => `Löper till ${d}`,
    stages: ['Planerade', 'Förproduktion', 'Produktion', 'Granskning', 'Levererade'],
    agreement: 'Avtal',
    agreementRef: (r: string) => `Avtal ${r}`,
    noAgreementYet: 'Inget avtal eller offert än.',
    quotes: 'Offerter',
    createQuote: 'Skapa offert',
    projectStatus: 'Projektstatus',
    projects: 'Projekt',
    lastDelivery: (m: string) => `Senaste leverans ${m}`,
    nothingDelivered: 'Inget levererat än',
    nextDeadline: (d: string) => `nästa deadline ${d}`,
    todayLower: 'i dag',
    clientSince: (m: string) => `kund sedan ${m}`,
    needsDoing: 'Behöver göras',
    nothingToDo: 'Inget behöver göras just nu.',
    todo: {
      periodOver: (d: string) => `Avropsperioden slutade ${d}: stäm av minimiåtagandet och kom överens om vad som händer sen`,
      renew: (d: string) => `Bestäm om förnyelse: uppsägning måste ske senast ${d}`,
      periodEnds: (d: string) => `Avropsperioden slutar ${d}: erbjud ett nytt avtal`,
      quote: (ref: string, d: string | null) => `Offert ${ref} väntar på svar${d ? `, giltig till ${d}` : ''}`,
      ended: (d: string | null) => `Avtalet avslutades${d ? ` ${d}` : ''}: erbjuda ett nytt?`,
      deadlineToday: 'Ett projekt har deadline idag',
      waiting: (n: number) => `${n} projekt väntar på kunden`,
      noContact: 'Ingen kontaktperson, så brief och kundportal kan inte skickas',
      quietSince: (m: string) => `Inget pågående sedan ${m}: dags att höra av sig?`,
      noProjects: 'Inga projekt än',
      noBrief: 'Den interna kundbriefen är inte skriven'
    },
    states: { active: 'Signerat', quote: 'Offert skickad', ended: 'Avslutat' },
    rows: { type: 'Typ', price: 'Pris', minimum: 'Minimum', term: 'Minsta avtalstid', period: 'Period', notice: 'Uppsägning', validTo: 'Giltig till', options: 'Tillval' },
    perMonth: (kr: string) => `${kr} / månad`,
    minimum: (n: number, pkg: string) => `${pl(n, 'produktion', 'produktioner')} ${pkg}`,
    fromUntil: (d: string) => `Från ${d}, tills vidare`,
    notice: (d: string) => `3 månader · senast ${d} för att sluta med avtalstiden`,
    passed: (pct: number, sub: boolean) => `${pct} % av ${sub ? 'minsta avtalstiden' : 'perioden'} har gått`
  }
);

const go = (path: string) => (e: Event) => (e.preventDefault(), navigate(path));
const val = (e: Event) => (e.target as HTMLInputElement | HTMLSelectElement).value;
/** "31 Oct" / "31 okt", with the year when it is not this year */
const day = (k: string, today: string, lang: Lang = 'en') => {
  const d = lang === 'sv' ? dmSv(k) : dm(k);
  return k.slice(0, 4) === today.slice(0, 4) ? d : `${d} ${k.slice(0, 4)}`;
};
const kr = (n: number) => `${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} kr`;

type Tone = 'ok' | 'warn' | 'crit' | 'info' | 'neutral';
/** The agreement in a few words: a coloured label and the date that matters. */
export function agreementLine(a: AgreementFact | null, today: string, lang: Lang = 'en'): { tone: Tone; label: string; detail: string | null } {
  const t = TX[lang];
  const d = (k: string) => day(k, today, lang);
  if (!a) return { tone: 'neutral', label: t.noAgreement, detail: null };
  if (a.state === 'quote') return { tone: 'info', label: t.quoteSent(a.sentAt ? d(a.sentAt.slice(0, 10)) : ''), detail: a.validUntil ? t.validTo(d(a.validUntil)) : a.ref };
  if (a.state === 'ended') return { tone: 'crit', label: t.ended(a.endedOn ? d(a.endedOn) : ''), detail: `${a.kind === 'subscription' ? t.subscription : t.perProduction} · ${a.packageKey}` };
  const p = a.period;
  if (a.kind === 'subscription') {
    const label = `${t.subscription} · ${a.packageKey}`;
    if (!p) return { tone: 'ok', label, detail: null };
    if (p.phase === 'upcoming') return { tone: 'ok', label, detail: t.starts(d(p.start)) };
    if (p.phase === 'binding' && p.renewSoon) return { tone: 'warn', label: t.noticeBy(d(p.noticeBy!)), detail: `${label} · ${t.bindsTo(d(p.end!))}` };
    if (p.phase === 'binding') return { tone: 'ok', label, detail: t.BindsTo(d(p.end!)) };
    return { tone: 'ok', label, detail: t.untilNotice };
  }
  const label = `${t.perProduction} · ${a.callOffKeys.length ? a.callOffKeys.join(', ') : a.packageKey}`;
  if (!p) return { tone: 'ok', label, detail: null };
  if (p.phase === 'period_over') return { tone: 'crit', label: t.periodEnded(d(p.end!)), detail: label };
  if (p.renewSoon) return { tone: 'warn', label: t.periodEnds(d(p.end!)), detail: label };
  return { tone: 'ok', label, detail: p.phase === 'upcoming' ? t.starts(d(p.start)) : a.minProductions ? t.minBy(a.minProductions, d(p.end!)) : t.runsTo(d(p.end!)) };
}

type Filter = 'all' | 'starting' | 'active' | 'renew' | 'none' | 'waiting' | 'quiet';
type Sort = 'name' | 'next' | 'ongoing' | 'agreement';
const FILTERS: { key: Filter; agreements?: boolean; starting?: boolean }[] = [{ key: 'all' }, { key: 'starting', starting: true }, { key: 'active', agreements: true }, { key: 'renew', agreements: true }, { key: 'none', agreements: true }, { key: 'waiting' }, { key: 'quiet' }];
const active = (c: ClientRow) => c.agreement?.state === 'active';
const renew = (c: ClientRow) => Boolean(active(c) && c.agreement!.period?.renewSoon);
const MATCH: Record<Filter, (c: ClientRow) => boolean> = {
  all: () => true,
  starting: (c) => Boolean(c.start),
  active,
  renew,
  none: (c) => !active(c),
  waiting: (c) => c.waitingOnClient > 0,
  quiet: (c) => c.quiet
};
const ongoing = (c: ClientRow) => c.projects.planned + c.projects.active;
const SORTS: Record<Sort, (a: ClientRow, b: ClientRow) => number> = {
  name: () => 0,
  next: (a, b) => (a.nextDeadline ?? '9').localeCompare(b.nextDeadline ?? '9'),
  ongoing: (a, b) => ongoing(b) - ongoing(a),
  agreement: (a, b) => {
    const k = (c: ClientRow) => (active(c) ? `0${c.agreement!.period?.actBy ?? '9'}` : c.agreement?.state === 'quote' ? '1' : '2');
    return k(a).localeCompare(k(b));
  }
};

function ProjectBar(p: { c: ClientFacts }) {
  const { planned, active: act, delivered } = p.c.projects;
  const total = planned + act + delivered;
  if (!total) return null;
  return (
    <span class="cl-bar" aria-hidden="true">
      <i class="cl-bar-d" style={`width:${(delivered / total) * 100}%`} />
      <i class="cl-bar-a" style={`width:${(act / total) * 100}%`} />
      <i class="cl-bar-p" style={`width:${(planned / total) * 100}%`} />
    </span>
  );
}

export function ClientsList(p: { api: Api }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<{ clients: ClientRow[]; today: string; showAgreements: boolean; canImport?: boolean } | null>(() => peek(p.api, '/api/clients/overview') ?? null);
  const [err, setErr] = useState<unknown>(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('name');
  const [former, setFormer] = useState(false);
  const [plan, setPlan] = useState<MondayPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => p.api.get<{ clients: ClientRow[]; today: string; showAgreements: boolean; canImport?: boolean }>('/api/clients/overview').then(setV, setErr);
  useEffect(() => void load(), []);
  // the whole import: clients and contacts in one call, then Client Notes a page at a time until Monday has no more
  const runImport = async () => {
    const r = await p.api.post<MondayPlan>('/api/clients/import-monday', {});
    let notes = 0;
    let cursor: string | null = null;
    do {
      const step: NotesStep = await p.api.post<NotesStep>('/api/clients/import-monday/notes', cursor ? { cursor } : {});
      notes += step.created;
      cursor = step.cursor;
      if (cursor) toast({ text: t.importNotesNow(notes) });
    } while (cursor);
    // then every project in Video Management as history on the client cards (0052), a page at a time
    let projects = 0;
    do {
      const step: { saved: number; cursor: string | null } = await p.api.post<{ saved: number; cursor: string | null }>('/api/clients/import-monday/projects', cursor ? { cursor } : {});
      projects += step.saved;
      cursor = step.cursor;
      if (cursor) toast({ text: t.importProjectsNow(projects) });
    } while (cursor);
    toast({ text: t.imported(r.clients.created, r.contacts.created, notes, projects) });
    await load();
  };
  const all = v?.clients ?? [];
  const rows = former ? all : all.filter(current);
  const today = v?.today ?? '';
  const agr = Boolean(v?.showAgreements);
  const needle = q.trim().toLocaleLowerCase('sv');
  const shown = rows
    .filter((c) => !needle || c.name.toLocaleLowerCase('sv').includes(needle) || c.code.toLocaleLowerCase('sv').includes(needle))
    .filter(MATCH[filter])
    .sort((a, b) => SORTS[sort](a, b) || a.name.localeCompare(b.name, 'sv'));
  const nActive = rows.filter(active).length;
  const waiting = rows.reduce((s, c) => s + c.waitingOnClient, 0);
  const pick = (f: Filter) => () => setFilter(filter === f ? 'all' : f);
  const stat = (f: Filter, k: string, value: number, d: string, tone?: 'warn') => (
    <button type="button" class={`stat cl-stat${tone && value ? ` stat--${tone}` : ''}`} aria-pressed={filter === f} onClick={pick(f)}>
      <span class="stat-k">{k}</span>
      <span class="stat-v">{value}</span>
      <span class="stat-d">{d}</span>
    </button>
  );
  return (
    <div class="screen">
      <PageHead title={t.clients} sub={v ? t.sub(rows.length, agr ? nActive : null) : undefined} />
      {v ? <AccessRequests api={p.api} kind="client" clients={v.clients} onChanged={() => void load()} /> : null}
      {plan ? (
        <ConfirmDialog
          title={t.importTitle}
          body={
            <>
              <p>{t.importClients(plan.clients)}</p>
              <p>{t.importContacts(plan.contacts)}</p>
              {plan.agreements ? <p>{t.importAgreements(plan.agreements)}</p> : null}
              {plan.notes ? <p>{t.importNotes(plan.notes)}</p> : null}
              {plan.clients.duplicates.length ? <p class="small muted">{t.importDup(plan.clients.duplicates)}</p> : null}
              <p class="small muted">{t.importSafe}</p>
            </>
          }
          confirmLabel={t.importGo}
          onClose={() => setPlan(null)}
          onConfirm={runImport}
        />
      ) : null}
      {err ? (
        <ErrorBox error={err} />
      ) : !v ? (
        <Loading />
      ) : (
        <>
          <div class="stats cl-stats" role="group" aria-label={t.attention} data-tour="client-stats">
            {agr ? stat('active', t.statActive[0], nActive, t.statActive[1]) : null}
            {agr ? stat('renew', t.statRenew[0], rows.filter(renew).length, t.statRenew[1], 'warn') : null}
            {stat('waiting', t.statWaiting, waiting, t.waitingD(waiting, rows.filter(MATCH.waiting).length), 'warn')}
            {stat('quiet', t.statQuiet[0], rows.filter(MATCH.quiet).length, t.statQuiet[1])}
          </div>
          <div class="cl-tools">
            <label class="field clients-search" data-tour="client-search">
              <span class="label">{t.search}</span>
              <input class="input" type="search" id="cl-q" value={q} placeholder={t.nameOrCode} onInput={(e) => setQ(val(e))} />
            </label>
            <div class="chips" role="group" aria-label={t.show}>
              {FILTERS.filter((f) => (agr || !f.agreements) && (!f.starting || rows.some((c) => c.start))).map((f) => (
                <button key={f.key} type="button" class="chip" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                  {t.filters[f.key]}
                </button>
              ))}
            </div>
            {all.length !== all.filter(current).length ? (
              <label class="check">
                <input type="checkbox" checked={former} onChange={(e) => setFormer((e.target as HTMLInputElement).checked)} /> {t.former}
              </label>
            ) : null}
            {v.canImport ? (
              <button
                type="button"
                class="btn"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    setPlan(await p.api.post<MondayPlan>('/api/clients/import-monday', { dryRun: true }));
                  } catch (e) {
                    setErr(e);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t.fromMonday}
              </button>
            ) : null}
            <label class="field cl-sort">
              <span class="label">{t.sort}</span>
              <select class="input" id="cl-sort" value={sort} onChange={(e) => setSort(val(e) as Sort)}>
                {(['name', 'next', 'ongoing', 'agreement'] as Sort[])
                  .filter((k) => agr || k !== 'agreement')
                  .map((k) => (
                    <option key={k} value={k}>
                      {t.sorts[k]}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          {!shown.length ? (
            <p class="muted">{needle ? t.noMatch(q) : t.noneInView}</p>
          ) : (
            <div class={`cl-list${agr ? ' cl-list--agr' : ''}`}>
              <div class="cl-head" aria-hidden="true">
                <span>{t.cols.client}</span>
                {agr ? <span>{t.cols.agreement}</span> : null}
                <span>{t.cols.projects}</span>
                <span>{t.cols.next}</span>
                <span>{t.cols.waiting}</span>
                <span>{t.cols.last}</span>
              </div>
              <ul class="list cl-rows">
                {shown.map((c, i) => {
                  const a = agr ? agreementLine(c.agreement ?? null, today, lang) : null;
                  return (
                    <li key={c.id} data-tour={i === 0 ? 'client-list' : undefined}>
                      <a class="cl-row" href={`/clients/${c.id}`} onClick={go(`/clients/${c.id}`)} title={c.code}>
                        <span class="cl-c cl-name">
                          <strong>{c.name}</strong>
                          {t.tag[c.lifecycle ?? 'active'] ? <span class="pill pill--neutral">{t.tag[c.lifecycle ?? 'active']}</span> : null}
                          <span class="small muted cl-meta">
                            {c.producer ? (
                              <>
                                <Avatar name={c.producer.name} size="sm" decorative /> {c.producer.name.split(' ')[0]} ·{' '}
                              </>
                            ) : null}
                            {c.notes ? t.notes(c.notes, when(c.lastNoteAt!, lang)) : t.noNotes}
                          </span>
                          {c.start ? <ClientStartCell start={c.start} /> : null}
                        </span>
                        {a ? (
                          <span class="cl-c cl-agr">
                            <span class="cl-k">{t.cols.agreement}</span>
                            <span class={`pill pill--${a.tone}`}>{a.label}</span>
                            {a.detail ? <span class="small muted">{a.detail}</span> : null}
                          </span>
                        ) : null}
                        <span class="cl-c">
                          <span class="cl-k">{t.cols.projects}</span>
                          <span class="small">{c.projects.total ? t.projectsLine(ongoing(c), c.projects.delivered) : t.noProjects}</span>
                          <ProjectBar c={c} />
                        </span>
                        <span class="cl-c">
                          <span class="cl-k">{t.cols.next}</span>
                          {c.nextDeadline ? <span class={c.nextDeadline === today ? 'cl-today' : undefined}>{c.nextDeadline === today ? t.today : day(c.nextDeadline, today, lang)}</span> : <span class="muted">–</span>}
                        </span>
                        <span class="cl-c">
                          <span class="cl-k">{t.cols.waiting}</span>
                          {c.waitingOnClient ? <span class="pill pill--warn">{t.nProjects(c.waitingOnClient)}</span> : <span class="muted">–</span>}
                        </span>
                        <span class="cl-c">
                          <span class="cl-k">{t.cols.last}</span>
                          <span>{c.lastDelivery ? monthLabel(c.lastDelivery, lang) : <span class="muted">–</span>}</span>
                          {c.quiet ? <span class="small muted">{t.quiet}</span> : null}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const STAGES: ProjectStatus[][] = [['planned'], ['pre_production'], ['in_production'], ['in_review', 'delivering'], ['delivered', 'closed']];

/** What someone should do about this client now, most urgent first. */
export function needsDoing(f: ClientFacts, x: { contacts: number; briefWritten: boolean; today: string }, lang: Lang = 'en'): { tone: 'crit' | 'warn' | 'info' | 'muted'; text: string }[] {
  const t = TX[lang].todo;
  const d = (k: string) => day(k, x.today, lang);
  const out: { tone: 'crit' | 'warn' | 'info' | 'muted'; text: string }[] = [];
  const a = f.agreement;
  const p = a?.period;
  if (a?.state === 'active' && p?.phase === 'period_over') out.push({ tone: 'crit', text: t.periodOver(d(p.end!)) });
  else if (a?.state === 'active' && p?.renewSoon && a.kind === 'subscription') out.push({ tone: 'warn', text: t.renew(d(p.noticeBy!)) });
  else if (a?.state === 'active' && p?.renewSoon) out.push({ tone: 'warn', text: t.periodEnds(d(p.end!)) });
  if (a?.state === 'quote') out.push({ tone: 'info', text: t.quote(a.ref, a.validUntil ? d(a.validUntil) : null) });
  if (a?.state === 'ended') out.push({ tone: 'warn', text: t.ended(a.endedOn ? d(a.endedOn) : null) });
  if (f.nextDeadline === x.today) out.push({ tone: 'crit', text: t.deadlineToday });
  if (f.waitingOnClient) out.push({ tone: 'warn', text: t.waiting(f.waitingOnClient) });
  if (!x.contacts) out.push({ tone: 'crit', text: t.noContact });
  if (f.quiet) out.push({ tone: 'info', text: f.lastDelivery ? t.quietSince(monthLabel(f.lastDelivery, lang)) : t.noProjects });
  if (!x.briefWritten) out.push({ tone: 'muted', text: t.noBrief });
  return out;
}

/** The client card's top band: agreement, project status and what needs doing. `newQuote`: where Create quote goes. */
export function ClientFactsBand(p: { facts: ClientFacts; today: string; contacts: number; briefWritten: boolean; newQuote?: string }) {
  const lang = useLang();
  const t = TX[lang];
  const f = p.facts;
  const a = f.agreement;
  const todo = needsDoing(f, { contacts: p.contacts, briefWritten: p.briefWritten, today: p.today }, lang);
  return (
    <div class={`cf${a === undefined ? ' cf--two' : ''}`}>
      {a === undefined ? null : (
        <section class="cf-box" aria-labelledby="cf-a">
          {a ? (
            <AgreementBox a={a} today={p.today} />
          ) : (
            <>
              <h2 class="blk-title" id="cf-a">
                {t.agreement}
              </h2>
              <p class="muted small">{t.noAgreementYet}</p>
              {p.newQuote ? (
                <a class="btn btn-sm" href={p.newQuote} onClick={go(p.newQuote)}>
                  {t.createQuote}
                </a>
              ) : (
                <a class="small" href="/agreements" onClick={go('/agreements')}>
                  {t.quotes}
                </a>
              )}
            </>
          )}
        </section>
      )}
      <div class="cf-box">
        <h2 class="blk-title" id="cf-p">
          {t.projectStatus}
        </h2>
        <div class="cf-stages">
          {STAGES.map((of, i) => (
            <div key={of[0]} class="cf-stage">
              <span>{t.stages[i]}</span>
              <b>{of.reduce((n, k) => n + (f.byStatus[k] ?? 0), 0)}</b>
            </div>
          ))}
        </div>
        <p class="small muted">
          {[
            f.lastDelivery ? t.lastDelivery(monthLabel(f.lastDelivery, lang)) : t.nothingDelivered,
            f.nextDeadline ? t.nextDeadline(f.nextDeadline === p.today ? t.todayLower : day(f.nextDeadline, p.today, lang)) : null,
            f.firstMonth && f.firstMonth <= p.today.slice(0, 7) ? t.clientSince(monthLabel(f.firstMonth, lang)) : null
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      <section class="cf-box" aria-labelledby="cf-t">
        <h2 class="blk-title" id="cf-t">
          {t.needsDoing}
        </h2>
        {todo.length ? (
          <ul class="cf-todo">
            {todo.map((x) => (
              <li key={x.text} class={`cf-todo--${x.tone}`}>
                {x.text}
              </li>
            ))}
          </ul>
        ) : (
          <p class="muted small">{t.nothingToDo}</p>
        )}
      </section>
    </div>
  );
}

function AgreementBox(p: { a: AgreementFact; today: string }) {
  const lang = useLang();
  const t = TX[lang];
  const { a, today } = p;
  const d = (k: string) => day(k, today, lang);
  const per = a.period;
  const line = agreementLine(a, today, lang);
  const stateTone = a.state === 'active' ? 'ok' : a.state === 'quote' ? 'info' : 'crit';
  const pkg = a.kind === 'project' && a.callOffKeys.length ? a.callOffKeys.join(', ') : a.packageLevel ? `${a.packageKey} (${a.packageLevel[lang]})` : a.packageKey;
  const rows: { k: string; v: string; warn?: boolean }[] = [
    { k: t.rows.type, v: `${a.kind === 'subscription' ? t.subscription : t.perProduction} · ${pkg}` },
    ...(a.kind === 'subscription' && a.monthly ? [{ k: t.rows.price, v: t.perMonth(kr(a.monthly)) }] : []),
    ...(a.kind === 'project' && a.minProductions ? [{ k: t.rows.minimum, v: t.minimum(a.minProductions, a.packageKey) }] : []),
    ...(per ? [{ k: a.kind === 'subscription' ? t.rows.term : t.rows.period, v: per.end ? `${d(per.start)} – ${d(per.end)}` : t.fromUntil(d(per.start)) }] : []),
    ...(per?.noticeBy ? [{ k: t.rows.notice, v: t.notice(d(per.noticeBy)), warn: line.tone === 'warn' }] : []),
    ...(a.state === 'quote' && a.validUntil ? [{ k: t.rows.validTo, v: d(a.validUntil) }] : []),
    ...(a.options.length ? [{ k: t.rows.options, v: a.options.map((o) => o[lang]).join(', ') }] : [])
  ];
  // the notice day on the timeline: where it falls between start and end
  const at = (k: string) => (per?.end ? Math.min(100, Math.max(0, ((Date.parse(k) - Date.parse(per.start)) / (Date.parse(per.end) - Date.parse(per.start))) * 100)) : 0);
  return (
    <>
      <div class="cf-agr-head">
        <h2 class="blk-title" id="cf-a">
          <a href={`/agreements/${a.id}`} onClick={go(`/agreements/${a.id}`)}>
            {t.agreementRef(a.ref)}
          </a>
        </h2>
        <span class={`pill pill--${stateTone}`}>{t.states[a.state]}</span>
      </div>
      <dl class="cf-dl">
        {rows.map((r) => (
          <Fragment key={r.k}>
            <dt>{r.k}</dt>
            <dd class={r.warn ? 'cf-warn' : undefined}>{r.v}</dd>
          </Fragment>
        ))}
      </dl>
      {per?.end && per.progress !== null ? (
        <div class="cf-time" role="img" aria-label={t.passed(Math.round(per.progress * 100), a.kind === 'subscription')}>
          <span class="cf-time-track" />
          <span class={`cf-time-done${per.phase === 'period_over' ? ' cf-time-done--over' : ''}`} style={`width:${per.progress * 100}%`} />
          {per.noticeBy && per.noticeBy > per.start ? <span class="cf-time-mark" style={`left:${at(per.noticeBy)}%`} /> : null}
          <span class="cf-time-l">{d(per.start)}</span>
          <span class="cf-time-r">{d(per.end)}</span>
        </div>
      ) : null}
    </>
  );
}
